/*
 * PetalPal slide cart — tracking layer
 * ====================================
 * Emits every cart interaction to TWO destinations at once:
 *
 *   1. Pippy's first-party collector  →  POST /track/<tenant_key>
 *      Same envelope + same `client_id` as tracking/shopify_web_pixel.js, so
 *      cart events land in the SAME `behavioral_events` table and join cleanly
 *      to page_view / add_to_cart / begin_checkout / purchase. That is what
 *      makes `lookup_cro_funnel` + `lookup_visitor_journey` able to explain
 *      what happened inside the drawer, not just around it.
 *
 *   2. Microsoft Clarity  →  clarity("event", name) + clarity("set", k, v)
 *      So a session replay can be FILTERED by cart behaviour ("show me
 *      everyone who opened the gift note and abandoned"). Pairs with the
 *      existing client_id bridge (tracking/clarity_client_id_bridge.liquid):
 *      that snippet tags the recording with our client_id, this one tags it
 *      with what the shopper actually did.
 *
 * WHY NOT THE SHOPIFY WEB PIXEL: the custom pixel sandbox only receives the 7
 * standard customer events and cannot see DOM interaction inside a theme-
 * rendered drawer. Cart-internal behaviour has to be emitted from theme
 * context — here.
 *
 * CONSENT: hard-gated on Shopify's Customer Privacy API
 * (analyticsProcessingAllowed). No consent → no beacon, no Clarity tag. This
 * mirrors the pixel's gate so the two never disagree.
 *
 * FAILURE POLICY: every function is wrapped. Tracking must NEVER break the
 * cart — a shopper losing checkout because an analytics call threw is a far
 * worse outcome than a missing datapoint.
 */
(function (w) {
  'use strict';

  var COLLECTOR_BASE = 'https://pippy.co';
  var TENANT_KEY = 'petalpal-dev'; // matches PIPPY_TRACK_KEY server-side default
  var ENDPOINT = COLLECTOR_BASE + '/track/' + TENANT_KEY;
  var SCHEMA_VERSION = 1;

  // ─────────────────────────── consent ────────────────────────────
  // Shopify folds region + location + visitor choice into this one boolean,
  // so we never reason about geography ourselves (same rule as the pixel).
  function analyticsAllowed() {
    try {
      var cp = w.Shopify && w.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() === true;
      }
      // If the Customer Privacy API hasn't loaded we FAIL CLOSED. A missing
      // API is not consent.
      return false;
    } catch (e) { return false; }
  }

  // ─────────────────────────── identity ───────────────────────────
  // pp_cid is written by the Shopify web pixel from event.clientId
  // (browser.cookie.set). Reading it here means cart events carry the exact
  // same client_id as pixel events — that identity match is the whole point.
  function clientId() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)pp_cid=([^;]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  function uuid() {
    try {
      if (w.crypto && w.crypto.randomUUID) return w.crypto.randomUUID();
      var b = new Uint8Array(16);
      w.crypto.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
      var h = []; for (var i = 0; i < 16; i++) h.push((b[i] + 0x100).toString(16).slice(1));
      return h.slice(0,4).join('') + '-' + h.slice(4,6).join('') + '-' +
             h.slice(6,8).join('') + '-' + h.slice(8,10).join('') + '-' + h.slice(10).join('');
    } catch (e) {
      return 'pp-' + Date.now() + '-' + Math.random().toString(16).slice(2);
    }
  }

  // ───────────────────────── Clarity tagging ──────────────────────
  // "event" marks a named moment on the recording (filterable as a Smart
  // Event). "set" writes a custom tag key/value. We use both: the event for
  // "did this happen", the tag for "with what value".
  function clarityTag(name, props) {
    try {
      if (!w.clarity) return;               // loader shim queues calls, so this
      w.clarity('event', name);             // is safe pre-download
      if (props && props.clarity_tag) {
        w.clarity('set', name, String(props.clarity_tag).slice(0, 255));
      }
    } catch (e) { /* never break the cart */ }
  }

  // ───────────────────────── the emitter ──────────────────────────
  /**
   * Emit one cart event.
   * @param {string} name  must be in the server's _TRACK_EVENT_ALLOWLIST
   * @param {object} [fields] top-level schema columns (value, currency, qty,
   *                 product_id, variant_id, cart_token …)
   * @param {object} [props]  free-form detail → behavioral_events.props JSONB.
   *                 `props.clarity_tag` additionally becomes a Clarity tag value.
   */
  function emit(name, fields, props) {
    try {
      if (!analyticsAllowed()) return;
      var cid = clientId();
      // No client_id → the server rejects it anyway, and an event we can't
      // join to a journey has no value. Drop it rather than send noise.
      if (!cid) return;

      var payload = {
        schema_version: SCHEMA_VERSION,
        event_id: uuid(),
        client_id: cid,
        event_name: name,
        occurred_at: new Date().toISOString(),
        url: location.href,
        path: location.pathname,
        referrer: document.referrer || null,
        consent: { analytics: true },
        props: props || {}
      };
      if (fields) {
        for (var k in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, k) && fields[k] != null) {
            payload[k] = fields[k];
          }
        }
      }
      payload.props.surface = 'slide_cart';

      var body = JSON.stringify(payload);
      // text/plain keeps this a CORS "simple request" — no preflight, and the
      // collector already answers with Access-Control-Allow-Origin: *.
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }));
      } else {
        fetch(ENDPOINT, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' });
      }

      clarityTag(name, props);
    } catch (e) { /* never break the cart */ }
  }

  w.PPCartTrack = { emit: emit, clientId: clientId, allowed: analyticsAllowed };
})(window);
